// SandwichModal.js – build a sandwich from catalog items (bread, protein, cheese, condiment, topping)
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../shared';
import { catalogApi } from '../../../api/catalogApi.js';
import { consumeApi } from '../../../api/consumeApi.js';

const CATEGORIES = [
  { key: 'bread',     label: 'Bread',     required: true },
  { key: 'protein',   label: 'Protein',   required: true },
  { key: 'cheese',    label: 'Cheese',    required: false },
  { key: 'condiment', label: 'Condiment', required: false },
  { key: 'topping',   label: 'Topping',   required: false },
];

function CategoryRow({ category, options, selected, onAdd, onRemoveAt, onServingsChange }) {
  const [selectValue, setSelectValue] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    if (value) {
      const item = options.find((o) => String(o.id) === value);
      if (item) {
        onAdd(item);
        setSelectValue('');
      }
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
        {category.label}
        {category.required && <span style={{ color: '#b00020' }}> *</span>}
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select
          value={selectValue}
          onChange={handleChange}
          style={{
            minWidth: 160,
            padding: '8px 10px',
            borderRadius: 6,
            border: '1px solid #ccc',
            fontSize: 14,
          }}
        >
          <option value="">{category.required ? 'Select…' : 'None'}</option>
          {(options || []).map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
        {selected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {selected.map((entry, idx) => {
              const numServings = entry.numServings ?? 1;
              return (
                <span
                  key={`${category.key}-${idx}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 8px',
                    background: '#E8E0F0',
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <span>{entry.name}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 11, color: '#555' }}>serv:</span>
                    <input
                      type="number"
                      min={0.25}
                      max={99}
                      step={0.25}
                      value={numServings}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value, 10);
                        if (!Number.isNaN(v) && v >= 0.25) onServingsChange(idx, v);
                      }}
                      style={{
                        width: 52,
                        padding: '2px 4px',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                        fontSize: 12,
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => onRemoveAt(idx)}
                    aria-label={`Remove ${entry.name}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontSize: 16,
                      lineHeight: 1,
                      color: '#555',
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const MACRO_KEYS = [
  { key: 'calories',      label: 'Calories', unit: 'kcal' },
  { key: 'totalFatG',     label: 'Total fat', unit: 'g' },
  { key: 'saturatedFatG', label: 'Sat. fat',  unit: 'g' },
  { key: 'sodiumMg',      label: 'Sodium',   unit: 'mg' },
  { key: 'totalCarbG',    label: 'Carbs',    unit: 'g' },
  { key: 'proteinG',      label: 'Protein',  unit: 'g' },
  { key: 'fiberG',        label: 'Fiber',    unit: 'g' },
];

function sumMacros(selectedItems) {
  const totals = MACRO_KEYS.reduce((acc, { key }) => {
    acc[key] = 0;
    return acc;
  }, {});
  CATEGORIES.forEach((cat) => {
    (selectedItems[cat.key] || []).forEach((entry) => {
      const servings = Number(entry.numServings) || 1;
      MACRO_KEYS.forEach(({ key }) => {
        const v = entry[key];
        totals[key] += (Number(v) || 0) * servings;
      });
    });
  });
  return MACRO_KEYS.reduce((acc, { key }) => {
    acc[key] = Math.round((totals[key] || 0) * 10) / 10;
    return acc;
  }, {});
}

function formatEatenOnLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const SandwichModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [itemsByCategory, setItemsByCategory] = useState({
    bread: [],
    protein: [],
    cheese: [],
    condiment: [],
    topping: [],
  });

  // Selected items per category: array of item objects
  const [selected, setSelected] = useState({
    bread: [],
    protein: [],
    cheese: [],
    condiment: [],
    topping: [],
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await catalogApi.getSandwichItems();
        if (!resp?.status) {
          if (!cancelled) setError(resp?.msg || 'Failed to load sandwich items');
          return;
        }
        const rows = resp?.data?.rows ?? {};
        const normalized = {
          bread:     Array.isArray(rows.bread)     ? rows.bread     : [],
          protein:   Array.isArray(rows.protein)  ? rows.protein   : [],
          cheese:    Array.isArray(rows.cheese)   ? rows.cheese    : [],
          condiment: Array.isArray(rows.condiment) ? rows.condiment : [],
          topping:   Array.isArray(rows.topping)  ? rows.topping   : [],
        };
        if (!cancelled) setItemsByCategory(normalized);
      } catch (e) {
        console.error('getSandwichItems error', e);
        if (!cancelled) setError('Failed to load sandwich items');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const addItem = useCallback((category, item) => {
    const numServings = Number(item.numServings) || 1;
    setSelected((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), { ...item, numServings }],
    }));
  }, []);

  const setServingsAt = useCallback((category, index, numServings) => {
    const v = parseFloat(numServings, 10);
    if (Number.isNaN(v) || v < 0.25) return;
    setSelected((prev) => {
      const list = prev[category] || [];
      const next = list.map((entry, i) =>
        i === index ? { ...entry, numServings: v } : entry
      );
      return { ...prev, [category]: next };
    });
  }, []);

  const removeItemAt = useCallback((category, index) => {
    setSelected((prev) => {
      const list = prev[category] || [];
      const next = [...list];
      next.splice(index, 1);
      return { ...prev, [category]: next };
    });
  }, []);

  const canSubmit =
    selected.bread.length > 0 &&
    selected.protein.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const eatenOn = formatEatenOnLocal();
    const entries = CATEGORIES.flatMap((cat) =>
      (selected[cat.key] || []).map((entry) => ({
        itemID: Number(entry.id),
        servingsEaten: Number(entry.numServings) || 1,
      }))
    );
    try {
      const results = await Promise.all(
        entries.map((e) =>
          consumeApi.eatItem({
            itemID: e.itemID,
            eatenOn,
            servingsEaten: e.servingsEaten,
          })
        )
      );
      const failed = results.some((r) => !r?.status);
      if (failed) {
        const msg = results.find((r) => !r?.status)?.msg || 'Some items could not be added';
        setError(msg);
      } else {
        onSuccess?.();
        onClose?.();
      }
    } catch (e) {
      console.error('Sandwich bulk add error', e);
      setError(e?.message || 'Failed to add sandwich items');
    } finally {
      setSubmitting(false);
    }
  };

  const macros = sumMacros(selected);

  return (
    <Modal title="Build a sandwich" onClose={onClose}>
      <div style={{ minWidth: 320, maxWidth: 480 }}>
        {loading && <div style={{ opacity: 0.8, fontStyle: 'italic' }}>Loading sandwich items…</div>}
        {error && <div style={{ color: '#b00020', marginBottom: 12 }}>{error}</div>}

        {!loading && !error && (
          <>
            {CATEGORIES.map((cat) => (
              <CategoryRow
                key={cat.key}
                category={cat}
                options={itemsByCategory[cat.key]}
                selected={selected[cat.key] || []}
                onAdd={(item) => addItem(cat.key, item)}
                onRemoveAt={(idx) => removeItemAt(cat.key, idx)}
                onServingsChange={(idx, v) => setServingsAt(cat.key, idx, v)}
              />
            ))}

            <div
              style={{
                marginBottom: 20,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 8,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Totals</div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: 8,
                }}
              >
                {MACRO_KEYS.map(({ key, label, unit }) => (
                  <div key={key}>
                    <span style={{ fontSize: 12, color: '#555' }}>{label}</span>
                    <div style={{ fontWeight: 600 }}>
                      {macros[key] != null ? `${Number(macros[key])} ${unit}` : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: 8,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 8,
                  background: canSubmit && !submitting ? '#8E24AA' : '#ccc',
                  color: '#fff',
                  cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                }}
              >
                {submitting ? 'Adding…' : 'Add sandwich'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export { SandwichModal };
